// ─────────────────────────────────────────────────────────────────────────────
// NoiseEngine — fully procedural ambient sound synthesis with Web Audio API.
//
// No remote files, no network, no asset bundling. Every sound (white/pink/brown
// noise, rain, ocean, café, forest, fire, fan) is generated live from noise
// buffers + filters + LFOs + scheduled micro-events. This is what makes the
// sounds *actually work* in an MV3 extension where remote audio + CSP routinely
// break. Runs inside the offscreen document (full Web Audio access, allowed to
// autoplay under the AUDIO_PLAYBACK reason).
// ─────────────────────────────────────────────────────────────────────────────

export type NoiseId =
  | 'rain' | 'cafe' | 'forest' | 'ocean' | 'fire' | 'fan' | 'brown' | 'pink' | 'white';

interface Voice {
  output: GainNode;
  stop(): void;
}

function safeStop(node: { stop?: () => void; disconnect?: () => void }) {
  try { node.stop?.(); } catch { /* already stopped */ }
  try { node.disconnect?.(); } catch { /* already disconnected */ }
}

export class NoiseEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private voices = new Map<NoiseId, { voice: Voice; gain: GainNode }>();
  private whiteBuf?: AudioBuffer;
  private pinkBuf?: AudioBuffer;
  private brownBuf?: AudioBuffer;
  private tickTimer: ReturnType<typeof setInterval> | null = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  private ensure() {
    if (this.ctx) return;
    const Ctx: typeof AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 1;
    this.master.connect(this.ctx.destination);
    this.buildBuffers();
  }

  async resume() {
    this.ensure();
    if (this.ctx!.state === 'suspended') {
      try { await this.ctx!.resume(); } catch { /* ignore */ }
    }
  }

  /** Active white-noise voices (id → volume). */
  activeSounds(): Record<string, number> {
    const out: Record<string, number> = {};
    this.voices.forEach((v, id) => { out[id] = v.gain.gain.value; });
    return out;
  }

  // ── Noise buffers (built once, looped via many sources) ─────────────────────

  private buildBuffers() {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * 5);

    const white = ctx.createBuffer(1, len, ctx.sampleRate);
    const wd = white.getChannelData(0);
    for (let i = 0; i < len; i++) wd[i] = Math.random() * 2 - 1;
    this.whiteBuf = white;

    // Pink — Paul Kellet's refined filter
    const pink = ctx.createBuffer(1, len, ctx.sampleRate);
    const pd = pink.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      pd[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
    this.pinkBuf = pink;

    // Brown — integrated white noise
    const brown = ctx.createBuffer(1, len, ctx.sampleRate);
    const bd = brown.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      bd[i] = last * 3.5;
    }
    this.brownBuf = brown;
  }

  private loopSrc(buf: AudioBuffer): AudioBufferSourceNode {
    const s = this.ctx!.createBufferSource();
    s.buffer = buf;
    s.loop = true;
    s.start();
    return s;
  }

  // ── Voice builders ──────────────────────────────────────────────────────────

  private buildVoice(id: NoiseId): Voice {
    const ctx = this.ctx!;
    const out = ctx.createGain();
    out.gain.value = 1;
    const stops: Array<() => void> = [];

    const filter = (type: BiquadFilterType, freq: number, q?: number) => {
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = freq;
      if (q !== undefined) f.Q.value = q;
      return f;
    };

    switch (id) {
      case 'white': {
        const s = this.loopSrc(this.whiteBuf!);
        s.connect(out); stops.push(() => safeStop(s));
        break;
      }
      case 'pink': {
        const s = this.loopSrc(this.pinkBuf!);
        s.connect(out); stops.push(() => safeStop(s));
        break;
      }
      case 'brown': {
        const s = this.loopSrc(this.brownBuf!);
        s.connect(out); stops.push(() => safeStop(s));
        break;
      }
      case 'rain': {
        const s = this.loopSrc(this.whiteBuf!);
        const hp = filter('highpass', 400);
        const lp = filter('lowpass', 6500);
        s.connect(hp); hp.connect(lp); lp.connect(out);
        stops.push(() => safeStop(s));
        break;
      }
      case 'ocean': {
        const s = this.loopSrc(this.brownBuf!);
        const lp = filter('lowpass', 650);
        const wave = ctx.createGain(); wave.gain.value = 0.55;
        s.connect(lp); lp.connect(wave); wave.connect(out);
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.09; // ~11s swell
        const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.4;
        lfo.connect(lfoGain); lfoGain.connect(wave.gain); lfo.start();
        stops.push(() => { safeStop(s); safeStop(lfo); });
        break;
      }
      case 'fan': {
        const s = this.loopSrc(this.pinkBuf!);
        const bp = filter('bandpass', 480, 1.1);
        s.connect(bp); bp.connect(out);
        const lfo = ctx.createOscillator(); lfo.frequency.value = 11; // blade hum
        const lfoGain = ctx.createGain(); lfoGain.gain.value = 45;
        lfo.connect(lfoGain); lfoGain.connect(bp.frequency); lfo.start();
        stops.push(() => { safeStop(s); safeStop(lfo); });
        break;
      }
      case 'fire': {
        const s = this.loopSrc(this.brownBuf!);
        const lp = filter('lowpass', 1000);
        const bed = ctx.createGain(); bed.gain.value = 0.6;
        s.connect(lp); lp.connect(bed); bed.connect(out);
        const stopCrackle = this.scheduleBursts(out, 'fire');
        stops.push(() => { safeStop(s); stopCrackle(); });
        break;
      }
      case 'cafe': {
        const s = this.loopSrc(this.brownBuf!);
        const lp = filter('lowpass', 850);
        const murmur = ctx.createGain(); murmur.gain.value = 0.7;
        s.connect(lp); lp.connect(murmur); murmur.connect(out);
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.33;
        const lg = ctx.createGain(); lg.gain.value = 0.25;
        lfo.connect(lg); lg.connect(murmur.gain); lfo.start();
        const stopClink = this.scheduleBursts(out, 'cafe');
        stops.push(() => { safeStop(s); safeStop(lfo); stopClink(); });
        break;
      }
      case 'forest': {
        const s = this.loopSrc(this.pinkBuf!);
        const lp = filter('lowpass', 3200);
        const bed = ctx.createGain(); bed.gain.value = 0.3;
        s.connect(lp); lp.connect(bed); bed.connect(out);
        const stopBirds = this.scheduleBursts(out, 'forest');
        stops.push(() => { safeStop(s); stopBirds(); });
        break;
      }
    }

    return {
      output: out,
      stop() { stops.forEach(f => f()); try { out.disconnect(); } catch { /* */ } },
    };
  }

  // ── Scheduled micro-events (crackle / chirp / clink) ────────────────────────

  private scheduleBursts(out: GainNode, kind: 'fire' | 'cafe' | 'forest'): () => void {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (!active) return;
      if (kind === 'fire') this.crackle(out);
      else if (kind === 'cafe') this.clink(out);
      else this.chirp(out);
      const delay =
        kind === 'fire' ? 70 + Math.random() * 230 :
        kind === 'cafe' ? 1500 + Math.random() * 4500 :
                          2500 + Math.random() * 7000;
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, 300);
    return () => { active = false; clearTimeout(timer); };
  }

  private crackle(out: GainNode) {
    const ctx = this.ctx!;
    const dur = 0.03 + Math.random() * 0.05;
    const b = ctx.createBufferSource();
    b.buffer = this.whiteBuf!; b.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 1000 + Math.random() * 2500; bp.Q.value = 3;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    const peak = 0.15 + Math.random() * 0.3;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    b.connect(bp); bp.connect(g); g.connect(out);
    b.start(t); b.stop(t + dur + 0.02);
  }

  private chirp(out: GainNode) {
    const ctx = this.ctx!;
    const t0 = ctx.currentTime;
    const notes = 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < notes; i++) {
      const t = t0 + i * 0.12;
      const o = ctx.createOscillator(); o.type = 'sine';
      const f = 2000 + Math.random() * 2200;
      o.frequency.setValueAtTime(f, t);
      o.frequency.linearRampToValueAtTime(f * 1.5, t + 0.08);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.11, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
      o.connect(g); g.connect(out);
      o.start(t); o.stop(t + 0.13);
    }
  }

  private clink(out: GainNode) {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator(); o.type = 'triangle';
    o.frequency.value = 1200 + Math.random() * 1800;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.045, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
    o.connect(g); g.connect(out);
    o.start(t); o.stop(t + 0.27);
  }

  // ── Public: white-noise mixer ───────────────────────────────────────────────

  /** Set a sound to a target volume (0 stops it). Fades smoothly. */
  setSound(id: NoiseId, volume: number) {
    this.ensure();
    void this.resume();
    const existing = this.voices.get(id);

    if (volume <= 0) {
      if (existing) {
        this.fade(existing.gain, 0, 0.3);
        const { voice, gain } = existing;
        setTimeout(() => { voice.stop(); try { gain.disconnect(); } catch { /* */ } }, 360);
        this.voices.delete(id);
      }
      return;
    }

    if (existing) { this.fade(existing.gain, volume, 0.3); return; }

    const voice = this.buildVoice(id);
    const gain = this.ctx!.createGain();
    gain.gain.value = 0;
    voice.output.connect(gain);
    gain.connect(this.master!);
    this.fade(gain, volume, 0.4);
    this.voices.set(id, { voice, gain });
  }

  /** Apply a full mix at once (ids not present are stopped). */
  applyMix(mix: Record<string, number>) {
    // stop anything no longer in the mix
    for (const id of Array.from(this.voices.keys())) {
      if (!(id in mix) || mix[id] <= 0) this.setSound(id, 0);
    }
    for (const [id, vol] of Object.entries(mix)) {
      if (vol > 0) this.setSound(id as NoiseId, vol);
    }
  }

  stopAll() {
    this.voices.forEach(({ voice, gain }) => {
      this.fade(gain, 0, 0.3);
      setTimeout(() => { voice.stop(); try { gain.disconnect(); } catch { /* */ } }, 360);
    });
    this.voices.clear();
  }

  private fade(gain: GainNode, to: number, seconds: number) {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
    gain.gain.linearRampToValueAtTime(to, now + seconds);
  }

  // ── Public: alarm chime ─────────────────────────────────────────────────────

  /** Pleasant ascending bell arpeggio played when a session ends. */
  playAlarm(volume = 0.8, repeats = 2) {
    this.ensure();
    void this.resume();
    const ctx = this.ctx!;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    for (let r = 0; r < repeats; r++) {
      const base = ctx.currentTime + r * 1.1;
      notes.forEach((f, i) => this.bell(f, base + i * 0.16, volume));
    }
  }

  private bell(freq: number, t: number, vol: number) {
    const ctx = this.ctx!;
    [1, 2, 3].forEach((h, idx) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = freq * h;
      const g = ctx.createGain();
      const amp = vol * (idx === 0 ? 0.5 : idx === 1 ? 0.18 : 0.08);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(amp, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
      o.connect(g); g.connect(this.master!);
      o.start(t); o.stop(t + 1.7);
    });
  }

  // ── Public: ticking clock ───────────────────────────────────────────────────

  startTick(volume = 0.3) {
    this.ensure();
    void this.resume();
    this.stopTick();
    this.tickTimer = setInterval(() => this.tickOnce(volume), 1000);
  }

  stopTick() {
    if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null; }
  }

  private tickOnce(vol: number) {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const b = ctx.createBufferSource();
    b.buffer = this.whiteBuf!;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 2200; bp.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
    b.connect(bp); bp.connect(g); g.connect(this.master!);
    b.start(t); b.stop(t + 0.05);
  }
}
